import { defineStore } from 'pinia';

import { t } from '@/config/i18n';
import { RTL_LOCALE, STATIC_DATA_PATHS } from '@/enums/constants';
import type { TLocale } from '@/types/locales/locales.types';

import { useGlobalStore } from './global';

type State = {
  currentLocale: number;
  currentISO: string | undefined;
  availableLocales: TLocale[];
};

const NON_LATIN_FONT_LANGS = ['ja', 'ar', 'hi', 'cmn'];

function updateBodyFontClass(lang?: string) {
  const classList = document.body.classList;
  classList.remove(...NON_LATIN_FONT_LANGS.map(l => `lang-${l}`));

  if (lang && NON_LATIN_FONT_LANGS.includes(lang)) {
    classList.add(`lang-${lang}`);
  }
}

export const useLocaleStore = defineStore('locales', {
  state: (): State => ({
    currentLocale: 1,
    currentISO: undefined,
    availableLocales: [] as TLocale[]
  }),
  getters: {
    /**
     * Return list of locales
     * @param {State} state
     * @returns List of locales
     */
    getAvailableLocales: (state): TLocale[] => {
      return state.availableLocales;
    },
    /**
     * Return list of locales ISO code 369
     * @param {State} state
     * @returns List of iso codes 369
     */
    getISOLocales: (state): string[] => {
      const mapLocalesObjectsToISO = (loc: TLocale) => loc.iso_code_639;
      return state.availableLocales.map(mapLocalesObjectsToISO);
    },
    getCurrentLocale: (state): TLocale => {
      return state.availableLocales.find(loc => loc.id === state.currentLocale) as TLocale;
    },
    /**
     * Return current iso in 639 format
     *
     * @param {State} state
     * @returns Current iso 639 ('fr_FR, 'en_EN', 'es_ES')
     */
    getCurrentISO: state => {
      if (!state.currentISO) {
        state.currentISO = state.availableLocales.find(loc => loc.id === state.currentLocale)?.iso_code_639;
      }

      return state.currentISO;
    },
    /**
     * Get current lang
     */
    getCurrentLang: state => {
      return state.availableLocales.find(loc => loc.id === state.currentLocale)?.iso_code.split('_')[0];
    },
    /**
     * Check if current locale is RTL
     */
    getIsLocaleRtl: (state): boolean => {
      const currentLocale = state.availableLocales.find(loc => loc.id === state.currentLocale);
      if (currentLocale) {
        return RTL_LOCALE.includes(currentLocale.iso_code_639);
      }
      return false;
    },
    getFormattedAvailableLocales: (state): (TLocale & { title: string; localizedTitle: string; value: number })[] => {
      const list = state.availableLocales.map(locale => {
        return {
          ...locale,
          title: t(locale.language_key),
          localizedTitle: t(locale.localized_language_key),
          value: locale.id
        };
      });
      return list.sort((a, b) => {
        if (a.title < b.title) return -1;
        return 1;
      });
    }
  },
  actions: {
    loadLocales() {
      return new Promise<void>((resolve, reject) => {
        const url = `${useGlobalStore().getStaticDataUrl}${STATIC_DATA_PATHS.locales}`;
        fetch(url)
          .then(async response => {
            this.availableLocales = await response.json();
            const currentLang = this.getCurrentLang;
            this.applyDir();
            updateBodyFontClass(currentLang);
            resolve();
          })
          .catch(error => {
            reject(error);
          });
      });
    },
    setLocale(id: number) {
      this.currentLocale = id;
      // when setting the currentLocal, invalid stored currentISO
      this.currentISO = undefined;
    },
    applyDir() {
      const locale = this.getCurrentLocale;
      document.documentElement.lang = locale.iso_code_639;
      document.documentElement.dir = this.getIsLocaleRtl ? 'rtl' : 'ltr';
    }
  }
});
